// Add / edit a service call (admin). v1 rules carried: at least a development or an
// address; day change on edit = moveCall THEN editCall; the week selector appears only
// when working from the Job Database (enabled for add, fixed for edit).
import type { ComponentChildren } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import { weekdayStubs } from '../../lib/dates';
import { createWeek, schedAction } from '../../state/admin';
import type { EditSheetState } from '../../state/adminUi';
import { editSheet, jobsRefresh } from '../../state/adminUi';
import { loadWeeks, week, weeks } from '../../state/schedule';
import { toast } from '../../state/toast';
import { AdminSheet } from './AdminSheet';

const SLOT_PRESETS = ['First Stop', '8–12', '12–4', 'Anytime'];

function Field({
  label,
  children,
  htmlFor,
}: {
  label: string;
  children: ComponentChildren;
  htmlFor?: string;
}) {
  return (
    <div class="mt-3">
      <label class="block text-sm font-semibold text-navy-900" for={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputCls = 'tap mt-1 w-full rounded-xl border border-slate-300 px-3 py-2';

export function EditCallSheet({ state }: { state: EditSheetState }) {
  const close = () => (editSheet.value = null);
  const [day, setDay] = useState(state.day);
  const [weekOf, setWeekOf] = useState(state.weekOf);
  const [newWeekDate, setNewWeekDate] = useState('');
  const [showNewWeekRow, setShowNewWeekRow] = useState(false);
  const c = state.call;
  const initialSlot = c.slot ?? '8–12';
  const [slotSel, setSlotSel] = useState(
    SLOT_PRESETS.includes(initialSlot) ? initialSlot : '__custom__',
  );
  const [slotCustom, setSlotCustom] = useState(
    SLOT_PRESETS.includes(initialSlot) ? '' : initialSlot,
  );
  const [development, setDevelopment] = useState(c.development ?? '');
  const [lot, setLot] = useState(c.lot ?? '');
  const [address, setAddress] = useState(c.address ?? '');
  const [name, setName] = useState(c.name ?? '');
  const [phone, setPhone] = useState(c.phone ?? '');
  const [instructions, setInstructions] = useState(c.instructions ?? '');
  const [problem, setProblem] = useState(c.problem ?? '');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // Known developments for the datalist — every community on the loaded board.
  const devOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const d of week.value?.days ?? [])
      for (const cc of d.calls) if (cc.development) seen.add(cc.development);
    return [...seen].sort();
  }, []);

  const weekList = [...weeks.value].sort((a, b) => b.weekId.localeCompare(a.weekId));
  const stubs = weekdayStubs(
    weekList.find((w) => w.weekOf === weekOf || w.weekId === weekOf)?.weekId ?? weekOf,
  );

  async function addNewWeek() {
    if (!newWeekDate) return;
    setBusy(true);
    const w = await createWeek(newWeekDate);
    setBusy(false);
    if (w) {
      setWeekOf(w.weekId);
      setShowNewWeekRow(false);
      setNewWeekDate('');
      toast(`Week created: ${w.weekOf}`);
    }
  }

  async function save() {
    const slot = slotSel === '__custom__' ? slotCustom.trim().slice(0, 40) : slotSel;
    const call = {
      slot,
      development: development.trim(),
      lot: lot.trim(),
      address: address.trim(),
      name: name.trim(),
      phone: phone.trim(),
      instructions: instructions.trim(),
      problem: problem.trim(),
    };
    if (!call.development && !call.address) {
      setErr('Enter at least a development or an address.');
      return;
    }
    setErr('');
    setBusy(true);
    let ok: boolean;
    if (state.mode === 'edit' && state.id) {
      // Day change moves first, then the field patch (v1 ordering).
      if (day !== state.day) {
        const moved = await schedAction({ action: 'moveCall', weekOf, id: state.id, day, slot });
        ok =
          !!moved &&
          !!(await schedAction({ action: 'editCall', weekOf, id: state.id, patch: call }));
      } else {
        ok = !!(await schedAction({ action: 'editCall', weekOf, id: state.id, patch: call }));
      }
    } else {
      ok = !!(await schedAction({ action: 'addCall', weekOf, day, call }));
    }
    setBusy(false);
    if (ok) {
      toast('Saved.');
      if (state.fromDb) {
        jobsRefresh.value++;
        void loadWeeks().catch(() => {});
      }
      close();
    }
  }

  return (
    <AdminSheet
      title={state.mode === 'edit' ? 'Edit service call' : 'Add service call'}
      onClose={close}
      testId="edit-call-sheet"
    >
      {state.fromDb && (
        <Field label="Week" htmlFor="edWeek">
          <select
            id="edWeek"
            class={inputCls}
            disabled={state.mode === 'edit'}
            value={weekOf}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              if (v === '__new__') {
                setShowNewWeekRow(true);
                return;
              }
              setShowNewWeekRow(false);
              setWeekOf(v);
            }}
          >
            {!weekList.some((w) => w.weekId === weekOf || w.weekOf === weekOf) && (
              <option value={weekOf}>{weekOf}</option>
            )}
            {weekList.map((w) => (
              <option key={w.weekId} value={w.weekId}>
                {w.weekOf}
              </option>
            ))}
            {state.mode === 'add' && <option value="__new__">＋ New week…</option>}
          </select>
          {showNewWeekRow && (
            <div class="mt-2 flex gap-2">
              <input
                type="date"
                class={`${inputCls} mt-0 flex-1`}
                aria-label="Any date in the new week"
                value={newWeekDate}
                onInput={(e) => setNewWeekDate((e.target as HTMLInputElement).value)}
              />
              <button class="btn-ghost" onClick={addNewWeek} disabled={busy || !newWeekDate}>
                Create
              </button>
            </div>
          )}
        </Field>
      )}

      <Field label="Day" htmlFor="edDay">
        <select
          id="edDay"
          class={inputCls}
          value={day}
          onChange={(e) => setDay((e.target as HTMLSelectElement).value)}
        >
          {stubs.map((d) => (
            <option key={d.key} value={d.key}>
              {d.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Time slot" htmlFor="edSlot">
        <select
          id="edSlot"
          class={inputCls}
          value={slotSel}
          onChange={(e) => setSlotSel((e.target as HTMLSelectElement).value)}
        >
          {SLOT_PRESETS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
          <option value="__custom__">Custom…</option>
        </select>
        {slotSel === '__custom__' && (
          <input
            type="text"
            class={inputCls}
            maxLength={40}
            placeholder="Type a time frame…"
            value={slotCustom}
            onInput={(e) => setSlotCustom((e.target as HTMLInputElement).value)}
          />
        )}
      </Field>

      <Field label="Development" htmlFor="edDev">
        <input
          id="edDev"
          type="text"
          class={inputCls}
          list="devList"
          autocomplete="off"
          placeholder="Start typing…"
          maxLength={120}
          value={development}
          onInput={(e) => setDevelopment((e.target as HTMLInputElement).value)}
        />
        <datalist id="devList">
          {devOptions.map((d) => (
            <option key={d} value={d} />
          ))}
        </datalist>
      </Field>

      <Field label="Lot #" htmlFor="edLot">
        <input
          id="edLot"
          type="text"
          inputmode="numeric"
          class={inputCls}
          maxLength={40}
          value={lot}
          onInput={(e) => setLot((e.target as HTMLInputElement).value)}
        />
      </Field>

      <Field label="Address" htmlFor="edAddr">
        <input
          id="edAddr"
          type="text"
          class={inputCls}
          maxLength={200}
          value={address}
          onInput={(e) => setAddress((e.target as HTMLInputElement).value)}
        />
      </Field>
      <p class="mt-1 text-xs font-semibold text-slate-500">
        Enter at least a development or an address.
      </p>

      <Field label="Homeowner name (optional)" htmlFor="edName">
        <input
          id="edName"
          type="text"
          class={inputCls}
          maxLength={80}
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
        />
      </Field>

      <Field label="Phone (optional)" htmlFor="edPhone">
        <input
          id="edPhone"
          type="text"
          inputmode="tel"
          class={inputCls}
          maxLength={40}
          value={phone}
          onInput={(e) => setPhone((e.target as HTMLInputElement).value)}
        />
      </Field>

      <Field label="Notes for tech (optional)" htmlFor="edTip">
        <textarea
          id="edTip"
          rows={2}
          class={inputCls}
          maxLength={400}
          value={instructions}
          onInput={(e) => setInstructions((e.target as HTMLTextAreaElement).value)}
        />
      </Field>

      <Field label="Problem / notes" htmlFor="edProblem">
        <textarea
          id="edProblem"
          rows={3}
          class={inputCls}
          maxLength={600}
          placeholder="Describe the work…"
          value={problem}
          onInput={(e) => setProblem((e.target as HTMLTextAreaElement).value)}
        />
      </Field>

      {err && (
        <p class="mt-2 text-sm font-semibold text-missed-ink" role="alert">
          {err}
        </p>
      )}

      <div class="mt-4 flex gap-2">
        <button class="btn-ghost flex-1" onClick={close} disabled={busy}>
          Cancel
        </button>
        <button
          class="btn-primary flex-1"
          data-testid="edit-call-save"
          onClick={save}
          disabled={busy}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </AdminSheet>
  );
}
